/**
 * Stripe here is a redirect, not an embedded form.
 *
 * Checkout and the customer portal are hosted by Stripe: the backend creates
 * the session and this file sends the browser to the URL it returns. Nothing
 * renders a card field, so Stripe.js was never needed - `getStripe()` lived
 * here for months without a single caller, and the package it loaded stayed in
 * the bundle's dependency list. Loading it would have put a third-party script
 * and its fraud-detection iframe on every page that imported this module,
 * including settings.
 */

import { useI18nStore } from "../stores/i18nStore";
import type { Language } from "../types/i18n";
import { askImmediateStart } from "@/utils/immediateStart";

export type StripeTierName =
  | "FREE"
  | "PLUS"
  | "PREMIUM"
  | "PLUS_SINGLE"
  | "PREMIUM_SINGLE";

export type Currency = "usd" | "eur";

export interface PricingTier {
  id: string;
  name: string;
  displayName: string;
  price: number;
  currency: Currency;
  billingType: "free" | "recurring" | "one_time";
  durationDays: number | null;
  features: string[];
}

const EU_COUNTRIES = [
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "GR",
  "HU",
  "IE",
  "IT",
  "LV",
  "LT",
  "LU",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE",
];

/**
 * Detects currency from browser locale/timezone (best-effort, no IP lookup).
 * Falls back to "usd".
 */
export function detectCurrency(): Currency {
  const locale = navigator.language || "";
  const localeParts = locale.split(/[-_]/);
  const country =
    localeParts.length >= 2
      ? (localeParts[localeParts.length - 1] ?? "").toUpperCase()
      : "";
  if (EU_COUNTRIES.includes(country)) return "eur";
  const lang = (localeParts[0] ?? "").toLowerCase();
  if (["de", "fr", "es", "it", "pt", "nl"].includes(lang)) return "eur";
  return "usd";
}

/**
 * Get available pricing tiers, currency-resolved by locale/country
 */
export async function getPricingTiers(
  currency?: Currency,
): Promise<PricingTier[]> {
  const resolvedCurrency = currency ?? detectCurrency();
  const params = new URLSearchParams({ currency: resolvedCurrency });
  const response = await fetch(`/api/stripe/pricing?${params}`);
  const data = await response.json();

  if (!data.success) {
    throw new Error("Failed to fetch pricing tiers");
  }

  return data.data;
}

/**
 * The language the traveller has chosen in the app, read at the moment of the
 * call. Stripe's pages are shown in it; left to itself Stripe would follow the
 * browser's language, which need not be the one the traveller reads the app in.
 */
function currentLanguage(): Language {
  return useI18nStore.getState().language;
}

/**
 * Create Stripe Checkout Session and redirect to checkout.
 *
 * Asks for the immediate-start confirmation first (utils/immediateStart.ts).
 * Returns false when the traveller backs out of it, true once on the way to
 * Stripe.
 */
export async function createCheckoutSession(
  tier: StripeTierName,
  token: string,
  currency?: Currency,
): Promise<boolean> {
  if (!(await askImmediateStart(tier))) return false;
  const resolvedCurrency = currency ?? detectCurrency();
  const response = await fetch("/api/stripe/create-checkout-session", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      tier,
      currency: resolvedCurrency,
      locale: currentLanguage(),
      immediateStart: true,
    }),
  });

  const data = await response.json();

  if (!data.success || !data.data?.url) {
    throw new Error(data.error?.message || "Failed to create checkout session");
  }

  window.location.href = data.data.url;
  return true;
}

/**
 * Open Stripe Customer Portal for managing subscription
 */
export async function openCustomerPortal(token: string): Promise<void> {
  const response = await fetch("/api/stripe/create-portal-session", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ locale: currentLanguage() }),
  });

  const data = await response.json();

  if (!data.success || !data.data?.url) {
    throw new Error(data.error?.message || "Failed to create portal session");
  }

  window.location.href = data.data.url;
}

export interface BillingDocument {
  id: string;
  number: string | null;
  status: string;
  /** Smallest unit of the currency, as Stripe reports it. */
  amount: number;
  currency: string;
  created: string;
  periodStart: string | null;
  periodEnd: string | null;
  description: string | null;
  /** Stripe's PDF — the invoice of record. */
  pdfUrl: string | null;
  /** Hosted receipt or payment page. */
  hostedUrl: string | null;
}

/**
 * The traveller's invoices and receipts.
 *
 * Read straight from Stripe each time. We keep no copy: an invoice is a
 * financial document and a second version of it in our database is a second
 * version that can go stale after a refund or a correction.
 */
export async function fetchBillingDocuments(
  token: string,
): Promise<BillingDocument[]> {
  const response = await fetch("/api/stripe/invoices", {
    headers: { Authorization: `Bearer ${token}` },
  });

  const data = await response.json();
  if (!data.success) {
    throw new Error(data.error?.message || "Failed to load invoices");
  }
  return data.data.documents as BillingDocument[];
}
